// Meeting lifecycle: the single source of truth for "is this meeting over?" — the point past which a
// meeting becomes a read-only artifact (no joining, no editing; view + delete only). Both the meetings
// list and the read-only view panel read this so the lock is consistent everywhere.

import { sanitizeDateTimeInput } from '@/utils/dateTimeFormatters'
import type { MeetingTouchPointPropertiesDto } from '@/types/touchPoint'

// Webster bot states that only occur once the call itself has ended — the bot has left and recall.ai
// is post-processing (Processing) or finished (Done). Either means the meeting is over regardless of
// the scheduled end time, which is exactly the "meetings that wrap up early" case.
const CONCLUDED_BOT_STATUSES = new Set(['Processing', 'Done'])

/**
 * "Now" as a wall-clock string (`YYYY-MM-DDTHH:mm:ss`) in the given IANA zone, so it compares directly
 * against the server's zoned wall-clock meeting times. The dashboard has no tz library, so this leans
 * on the platform: Intl renders the current instant into the target zone and formatToParts keeps it
 * locale-proof. Returns null for an unrecognized zone (Intl throws) so the caller can skip the clock
 * check rather than crash the list.
 */
function nowWallClockInZone(ianaZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(new Date())
    const get = (type: string): string => parts.find(p => p.type === type)?.value ?? '00'
    // Some engines emit hour "24" at midnight under hour12:false; normalize so the string stays sortable.
    const hour = get('hour') === '24' ? '00' : get('hour')
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`
  } catch {
    return null
  }
}

/**
 * Whether a meeting has concluded — no longer joinable or editable (view-only). True when Webster
 * reports the call finished (Processing/Done), OR the meeting's end wall-clock has passed in its own
 * timezone. The bot signal handles early finishes; the clock handles meetings with no bot at all
 * (unsupported URL, Webster opted out, failed capture) that simply ran their scheduled course.
 */
export function isMeetingConcluded(meeting?: MeetingTouchPointPropertiesDto | null): boolean {
  if (!meeting) return false
  if (meeting.BotStatus && CONCLUDED_BOT_STATUSES.has(meeting.BotStatus)) return true

  const endWall = sanitizeDateTimeInput(meeting.EndTime) // 'YYYY-MM-DDTHH:mm'
  if (!endWall || !meeting.TimeZone) return false
  const now = nowWallClockInZone(meeting.TimeZone)
  if (!now) return false
  // sanitize yields minute precision; pad to seconds so both sides are fixed-width YYYY-MM-DDTHH:mm:ss.
  const endAtSecond = endWall.length === 16 ? `${endWall}:00` : endWall
  return now >= endAtSecond
}
